import React from 'react';
import { Button, Col, Row, Card, Descriptions, Typography } from 'antd';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { InputField } from 'components/form/base/InputField';
import { useDeepEffect } from 'utils/useDeepEffect';
import { FormActions } from 'components/Layout/FormActions/FormActions';
import { PhoneNumberField } from 'components/form/base/PhoneNumberField';
import { ITouristItem } from 'store/slices/tourist/interfaces';
import { phonePlaceHoldersHash } from 'constants/phonePlaceholder';
import { TAuthTypes, authTypesValueTextHash } from 'constants/authTypes';

import { mapValues, validationSchema } from './formUtils';
import styles from './TouristForm.module.less';

type TProps = {
  initialValues: ITouristItem & { email: string };
  onFinish: (values: ITouristItem) => void;
  cancelButtonHandler: () => void;
  isLoading: boolean;
  authType: TAuthTypes;
};

export const TouristForm: React.FC<TProps> = ({
  initialValues,
  onFinish,
  cancelButtonHandler,
  isLoading,
  authType,
}) => {
  const resolver = yupResolver(validationSchema);

  const methods = useForm({
    defaultValues: initialValues,
    resolver,
  });

  const submit = (values: FieldValues) => {
    const preparedValues = mapValues(values);
    onFinish(preparedValues);
  };

  useDeepEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className='ant-form ant-form-vertical indent-top'
      >
        <Card title='Общая информация' bordered={false}>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <InputField
                name='lastName'
                label='Фамилия'
                required
                placeholder='Введите фамилию'
                maxLength={75}
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <InputField
                name='firstName'
                label='Имя'
                required
                placeholder='Введите имя'
                maxLength={75}
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <InputField name='email' label='Email' required disabled />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <PhoneNumberField
                name='phone'
                label='Телефон'
                placeholder={phonePlaceHoldersHash.mobilePhone}
              />
            </Col>
          </Row>
          <Row style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Descriptions title='' size='small' column={1} layout='vertical'>
                <Descriptions.Item
                  label='Тип регистрации'
                  className={styles.row}
                >
                  <Typography.Text type='secondary'>
                    {authTypesValueTextHash[authType]}
                  </Typography.Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        <FormActions>
          <Button onClick={cancelButtonHandler} disabled={isLoading}>
            Отмена
          </Button>
          <Button type='primary' htmlType='submit' loading={isLoading}>
            Сохранить
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
};
